// MUI-free copy of Ui-components/mvp-development/MvpCostTimeline.
import React, {memo, useState} from 'react';
import type {StaticImageData} from 'next/image';
import Container from '@/components/atoms/Container';
import Grid from '@/components/atoms/Grid';
import SectionHeader from '@/components/molecules/SectionHeader';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import styles from './MvpCostTimeline.module.scss';

interface CostScenario {
    id: string;
    label: string;
    cost: string;
    timeline: string;
    /** Omit when there is no scope summary; the "includes" row is then skipped. */
    scope?: string;
}

interface MvpCostTimelineProps {
    heading: string;
    /** Accepts nodes so the copy can carry an inline link. */
    subheading: React.ReactNode;
    highlight: {range: string; caption: string; image: string | StaticImageData};
    estimator: {title: string; description: string; scenarios: CostScenario[]};
    /** Optional button under the estimator. Renders only when both are supplied. */
    ctaText?: string;
    onCtaClick?: () => void;
}

const COVER_STYLE = {objectFit: 'cover'} as const;

// MUI ArrowOutward path (inlined: not in plain/Icon).
const ARROW_OUTWARD_PATH = 'M6 6v2h8.59L5 17.59 6.41 19 16 9.41V18h2V6z';

const MvpCostTimeline = ({heading, subheading, highlight, estimator, ctaText, onCtaClick}: MvpCostTimelineProps) => {
    const [activeId, setActiveId] = useState(estimator.scenarios[0]?.id);
    const activeIndex = Math.max(
        0,
        estimator.scenarios.findIndex((scenario) => scenario.id === activeId)
    );
    const activeScenario = estimator.scenarios[activeIndex];

    return (
        <div>
            <Container>
                <SectionHeader heading={heading} subheading={subheading} />

                <Grid container spacing={3}>
                    <Grid item xs={12} md={5} className={styles.gridItem}>
                        <div className={styles.highlightCard}>
                            <OptimizedImage
                                src={highlight.image}
                                alt={heading}
                                fill
                                sizes="(max-width: 900px) 100vw, 42vw"
                                style={COVER_STYLE}
                            />
                            <div className={styles.highlightScrim} />
                            <div className={styles.highlightContent}>
                                <p className={`font-headers ${styles.highlightRange}`}>{highlight.range}</p>
                                <p className={`font-regular-text ${styles.highlightCaption}`}>{highlight.caption}</p>
                            </div>
                        </div>
                    </Grid>

                    <Grid item xs={12} md={7} className={styles.gridItem}>
                        <div className={styles.estimatorCard}>
                            <h3 className={`font-headers ${styles.estimatorTitle}`}>{estimator.title}</h3>
                            <p className={`font-regular-text ${styles.estimatorDesc}`}>{estimator.description}</p>

                            <div className={styles.scenarioGrid}>
                                {estimator.scenarios.map((scenario) => {
                                    const active = scenario.id === activeScenario?.id;
                                    return (
                                        <button
                                            key={scenario.id}
                                            type="button"
                                            onClick={() => setActiveId(scenario.id)}
                                            aria-pressed={active}
                                            className={`font-regular-text ${styles.scenarioBtn} ${active ? styles.scenarioBtnActive : ''}`}
                                        >
                                            {scenario.label}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Every scenario's estimate is in the HTML (search engines read them all); only the selected
                                one shows, laid out exactly as before (`display: contents`). */}
                            {estimator.scenarios.map((scenario, idx) => (
                                <div key={scenario.id} className={idx === activeIndex ? styles.shownPanel : styles.hiddenPanel}>
                                    <div className={styles.detailPanel}>
                                        <p className={`font-headers ${styles.detailTitle}`}>{scenario.label}</p>

                                        <div className={styles.statRow}>
                                            <div className={styles.statTile}>
                                                <p className={`font-headers ${styles.statLabel}`}>Estimated Cost</p>
                                                <p className={`font-headers ${styles.statValue}`}>{scenario.cost}</p>
                                            </div>
                                            <div className={styles.statTile}>
                                                <p className={`font-headers ${styles.statLabel}`}>Timeline</p>
                                                <p className={`font-headers ${styles.statValue}`}>{scenario.timeline}</p>
                                            </div>
                                        </div>

                                        {/* The bar shows where a scenario sits on the investment scale, not a completion state. */}
                                        <div className={styles.scaleTrack}>
                                            <div
                                                className={styles.scaleFill}
                                                style={{width: `${((idx + 1) / estimator.scenarios.length) * 100}%`}}
                                            />
                                        </div>

                                        {scenario.scope && (
                                            <div className={styles.includeRow}>
                                                <div className={styles.includeDot} />
                                                <span className={`font-regular-text ${styles.includeText}`}>{scenario.scope}</span>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}

                            {ctaText && onCtaClick && (
                                <div className={styles.ctaWrap}>
                                    <button
                                        type="button"
                                        className={`animated-button button-bg-primary ${styles.ctaButton}`}
                                        onClick={onCtaClick}
                                    >
                                        {ctaText}
                                        {/* MUI Button endIcon slot; the class name is kept for any global `.MuiButton-endIcon` rules. */}
                                        <span className={`MuiButton-endIcon ${styles.endIcon}`}>
                                            <svg className={styles.endIconSvg} viewBox="0 0 24 24" focusable="false" aria-hidden="true">
                                                <path d={ARROW_OUTWARD_PATH} />
                                            </svg>
                                        </span>
                                    </button>
                                </div>
                            )}
                        </div>
                    </Grid>
                </Grid>
            </Container>
        </div>
    );
};

export default memo(MvpCostTimeline);
